import type { MCP } from "@/services/schemas/mcps-schemas";
import { getStatusChipStyle } from "@/lib/status-chip";

// Helpers for MCP server rows. MCP is a first-class entity (no auth_status /
// system_status fields like the old System row). Two distinct things are
// surfaced from what the entity carries:
//   - status                     → backend lifecycle string ("active" | "inactive")
//   - config.last_discovered_at  → whether discovery has ever run (connection liveness)

export function mcpEndpointUrl(server: MCP): string {
  return server.config?.endpoint_url?.trim() ?? "";
}

// Connection liveness — a binary FACT (has the server ever connected + discovered),
// distinct from lifecycle status. Shown under the "CONNECTION" label, never as "status".
export function isMcpConnected(server: MCP): boolean {
  return Boolean(server.config?.last_discovered_at);
}

// Lifecycle gate: reflects ONLY the backend lifecycle status. Connection
// liveness is a separate axis and must not silently force lifecycle inactive.
export function isInactive(server: MCP): boolean {
  return server.status === "inactive";
}

export type McpLifecycleKey = "active" | "inactive";

export interface McpLifecycle {
  key: McpLifecycleKey;
  // Pill bg + border + text classes.
  className: string;
  // Status-dot color class.
  dotClassName: string;
}

// Lifecycle (active/inactive) status chip — a SEPARATE axis from connection
// liveness (isMcpConnected). Keys map to i18n under `Mcp.lifecycle`.
const LIFECYCLE_STYLES: Record<
  McpLifecycleKey,
  { pill: string; dot: string }
> = {
  active: {
    pill: [
      getStatusChipStyle("active").backgroundClassName,
      getStatusChipStyle("active").borderClassName,
      getStatusChipStyle("active").textClassName,
    ].join(" "),
    dot: getStatusChipStyle("active").dotClassName,
  },
  inactive: {
    pill: [
      getStatusChipStyle("inactive").backgroundClassName,
      getStatusChipStyle("inactive").borderClassName,
      getStatusChipStyle("inactive").textClassName,
    ].join(" "),
    dot: getStatusChipStyle("inactive").dotClassName,
  },
};

export function deriveMcpLifecycle(server: MCP): McpLifecycle {
  const key: McpLifecycleKey = isInactive(server) ? "inactive" : "active";
  return {
    key,
    className: LIFECYCLE_STYLES[key].pill,
    dotClassName: LIFECYCLE_STYLES[key].dot,
  };
}
